import get from 'lodash/get';

const DEFAULT_ORGANIZER_LIMIT = 18;
const SMALL_CITY_KEY = 'small_city_fallback';
const FOLLOWED_BUCKET_KEY = 'followed_profiles';
const EVENT_TYPE = 'event';

/**
 * Wether a value is null
 * @param  {Object} item    Value to check
 * @return {Boolean}        If the value is not null
 */
const isNotNull = (item) => item !== null;

/**
 * Wether a bucket is a bucket full of events
 * @param  {String} options.type  Type of the bucket
 * @return {Boolean}              If the bucket is a event bucket
 */
const isAnEventBucket = ({ type }) => type === EVENT_TYPE;

/**
 * Wether the bucket is the "From organizers you follow"
 * @param  {String} options.key     Key of the bucket
 * @return {Boolean}                Whether the key is 'followed_profiles'
 */
const isNotFromOrganizersYouFollowBucket = ({ key }) =>
    key !== FOLLOWED_BUCKET_KEY;

/**
 * Extracts the organizer Id from the event object
 * @param  {String} options.primaryOrganizerId      Organizer Id
 * @return {String}                                 The organizer Id
 */
const getOrganizerId = ({ primaryOrganizerId }) => primaryOrganizerId;

/**
 * Removes duplicated values of the array
 * @param  {String[]} acc   Pruned array
 * @param  {String} item    Value to examine
 * @return {String[]}       Duplication free array
 */
const pruneDuplicates = (acc, item) =>
    acc.includes(item) ? acc : [...acc, item];

/**
 * Adds up the events from the bucket
 * @param  {Object[]} acc    Events
 * @param  {Object} bucket   Bucket of events
 * @return {Object[]}        List of all events
 */
const reduceEvents = (acc, bucket) => [...acc, ...get(bucket, 'events', [])];

/**
 * Grabs the event object from the entities list of the state
 * @param  {Object} state     Object with the application state
 * @param  {String} id          Event id to pick
 * @return {Object}             Event object or Null if not found
 */
const getEventObjectFromEntities = (state, id) =>
    state.content.entities[id] ? state.content.entities[id] : null;

/**
 * Pick Event ids from the entityContext object, taking into account the type of response
 * @param  {Object} state     Object with the application state
 * @return {String[]}           Array of all event ids in the context
 */
const getEventIdsFromContext = (state) => {
    const firstBucket = get(state, ['content', 'entityContext', 0], {});
    const isLargeCity = firstBucket.key !== SMALL_CITY_KEY;
    // const isLargeCityBis = (state.content.entityContext.length > 4);

    let allEventIds = [];

    if (isLargeCity) {
        allEventIds = state.content.entityContext
            .filter(isAnEventBucket)
            .filter(isNotFromOrganizersYouFollowBucket)
            .reduce(reduceEvents, []);
    } else {
        allEventIds = firstBucket.events;
    }

    return allEventIds;
};

/**
 * Picks a limited list of Organizer Ids from the browse page state
 * @param  {Object} state               Object with the application state
 * @param  {Number} limit[_limit=18]    Maximum organizer ids to pick
 * @return {String[]}                   List of prioritized (from the context) organizer events
 */
export const getOrganizersFromEvents = (
    state,
    limit = DEFAULT_ORGANIZER_LIMIT,
) =>
    getEventIdsFromContext(state)
        .map(getEventObjectFromEntities.bind(null, state))
        .filter(isNotNull)
        .map(getOrganizerId)
        .filter(isNotNull)
        .reduce(pruneDuplicates, [])
        .splice(0, limit);
